import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  InputAdornment,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  Snackbar,
  useTheme
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon
} from '@mui/icons-material';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
} from 'chart.js';
import dataQualityService from '../../services/dataQualityService';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler
);

const QualityDashboard = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [entityFilter, setEntityFilter] = useState('all');
  const [timeRange, setTimeRange] = useState('30d');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const [dashboardData, setDashboardData] = useState({
    totalAssessments: 0,
    avgScore: 0,
    entityCount: 0,
    qualityIssues: 0,
    qualityMetrics: {},
    topRules: []
  });

  const [qualityTrend, setQualityTrend] = useState({
    labels: [],
    datasets: []
  });

  const fetchDashboardData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [metrics, dimensions, trends, topRules] = await Promise.all([
        dataQualityService.getDashboardMetrics(),
        dataQualityService.getDashboardDimensions(),
        dataQualityService.getDashboardTrends(timeRange),
        dataQualityService.getTopFailingRules(6)
      ]);

      const avgScore = parseFloat(metrics?.avgScore ?? metrics?.avg_score ?? 0);
      const totalAssessments = parseInt(metrics?.totalAssessments ?? metrics?.total_assessments ?? 0);

      setDashboardData({
        totalAssessments,
        avgScore: Math.round(avgScore),
        entityCount: (metrics?.entityCounts?.organizations ?? metrics?.entity_counts?.organizations ?? 0) + (metrics?.entityCounts?.facilities ?? metrics?.entity_counts?.facilities ?? 0),
        qualityIssues: Math.round(100 - avgScore),
        qualityMetrics: {
          temporal: Math.round(((dimensions?.dimensions ?? dimensions ?? []).find(d => d.name === 'Temporal')?.normalized ?? 0)),
          geographical: Math.round(((dimensions?.dimensions ?? dimensions ?? []).find(d => d.name === 'Geographical')?.normalized ?? 0)),
          technological: Math.round(((dimensions?.dimensions ?? dimensions ?? []).find(d => d.name === 'Technological')?.normalized ?? 0)),
          reliability: Math.round(((dimensions?.dimensions ?? dimensions ?? []).find(d => d.name === 'Reliability')?.normalized ?? 0)),
          completeness: Math.round(((dimensions?.dimensions ?? dimensions ?? []).find(d => d.name === 'Coverage')?.normalized ?? 0))
        },
        topRules: (topRules || []).map(r => ({
          name: r.ruleName || r.rule_name || r.name || 'Unknown',
          errors: r.errorCount || r.failure_count || r.errors || 0,
          impact: r.impactPercent || r.impact_percentage || r.impact || 0
        }))
      });

      if (trends && trends.length > 0) {
        setQualityTrend({
          labels: trends.map(t => new Date(t.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })),
          datasets: [
            {
              label: 'Average Quality Score',
              data: trends.map(t => Math.round(parseFloat(t.avgScore || t.avg_score || 0))),
              borderColor: theme.palette.primary.main,
              backgroundColor: theme.palette.primary.main + '20',
              fill: true,
              tension: 0.4
            },
            {
              label: 'Assessment Count',
              data: trends.map(t => parseInt(t.count || 0)),
              borderColor: theme.palette.success.main,
              backgroundColor: theme.palette.success.main + '20',
              fill: false,
              tension: 0.4,
              yAxisID: 'y1'
            }
          ]
        });
      }

      setSnackbar({ open: true, message: 'Quality data loaded from ELM database', severity: 'success' });
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(`Failed to load quality data: ${err.response?.data?.error || err.message}`);
    } finally {
      setLoading(false);
    }
  }, [timeRange, theme]);

  useEffect(() => {
    fetchDashboardData();
  }, [fetchDashboardData]);

  const getQualityColor = (value) => {
    if (typeof value !== 'number') return theme.palette.grey[500];
    if (value >= 80) return theme.palette.success.main;
    if (value >= 60) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  const getQualityIcon = (value) => {
    if (typeof value !== 'number') return <WarningIcon />;
    if (value >= 80) return <CheckCircleIcon />;
    if (value >= 60) return <WarningIcon />;
    return <ErrorIcon />;
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { usePointStyle: true, padding: 20 } },
      title: { display: false }
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        ticks: { callback: (v) => v + '%' }
      },
      y1: {
        position: 'right',
        beginAtZero: true,
        grid: { drawOnChartArea: false },
        ticks: { stepSize: 1 }
      },
      x: { grid: { display: false } }
    },
    interaction: { intersect: false, mode: 'index' },
    elements: { point: { radius: 4, hoverRadius: 8 } }
  };

  if (loading) {
    return (
      <Box sx={{ width: '100%', mt: 2 }}>
        <LinearProgress />
        <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
          Loading quality dashboard...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <Alert severity="success" sx={{ mb: 2 }} icon={<CheckCircleIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', justifyContent: 'space-between' }}>
          <Typography variant="body2">
            <strong>Live Quality Dashboard</strong> - Data from Open Footprint &amp; Carbon Measure databases | PACT DQI Scoring
          </Typography>
          <Chip label="OFP CONNECTED" color="success" size="small" sx={{ fontWeight: 'bold' }} />
        </Box>
      </Alert>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2, flexWrap: 'wrap' }}>
        <TextField
          placeholder="Search entities..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          size="small"
          sx={{ minWidth: 200 }}
          InputProps={{
            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
          }}
        />
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Entity Type</InputLabel>
          <Select value={entityFilter} onChange={(e) => setEntityFilter(e.target.value)} label="Entity Type">
            <MenuItem value="all">All Types</MenuItem>
            <MenuItem value="organization">Organization</MenuItem>
            <MenuItem value="facility">Facility</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <InputLabel>Time Range</InputLabel>
          <Select value={timeRange} onChange={(e) => setTimeRange(e.target.value)} label="Time Range">
            <MenuItem value="7d">Last 7 days</MenuItem>
            <MenuItem value="30d">Last 30 days</MenuItem>
            <MenuItem value="90d">Last 90 days</MenuItem>
            <MenuItem value="365d">Last year</MenuItem>
          </Select>
        </FormControl>
        <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchDashboardData} disabled={loading}>
          Refresh
        </Button>
      </Box>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">TOTAL ASSESSMENTS</Typography>
              <Typography variant="h4">{dashboardData.totalAssessments.toLocaleString()}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <TrendingUpIcon color="success" fontSize="small" />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>Quality assessments</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">AVERAGE SCORE</Typography>
              <Typography variant="h4" color={getQualityColor(dashboardData.avgScore)}>
                {dashboardData.avgScore}%
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                {getQualityIcon(dashboardData.avgScore)}
                <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>PACT DQI Score</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">ENTITIES TRACKED</Typography>
              <Typography variant="h4">{dashboardData.entityCount}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <TrendingUpIcon color="success" fontSize="small" />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>Organizations + Facilities</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom variant="body2">QUALITY ISSUES</Typography>
              <Typography variant="h4" color="error.main">{dashboardData.qualityIssues}%</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <TrendingDownIcon color="success" fontSize="small" />
                <Typography variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>Gap from 100%</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>PACT DQI Dimensions</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Quality scores across 5 PACT Data Quality Indicator dimensions (0-100 normalized)
            </Typography>
            <Box sx={{ mt: 2 }}>
              {Object.entries({
                'Temporal': dashboardData.qualityMetrics?.temporal || 0,
                'Geographical': dashboardData.qualityMetrics?.geographical || 0,
                'Technological': dashboardData.qualityMetrics?.technological || 0,
                'Reliability': dashboardData.qualityMetrics?.reliability || 0,
                'Completeness': dashboardData.qualityMetrics?.completeness || 0
              }).map(([key, value]) => (
                <Box key={key} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">{key}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {getQualityIcon(value)}
                      <Typography variant="body2" sx={{ ml: 1 }}>{value}%</Typography>
                    </Box>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={value}
                    sx={{
                      height: 6, borderRadius: 3,
                      backgroundColor: theme.palette.grey[200],
                      '& .MuiLinearProgress-bar': { backgroundColor: getQualityColor(value), borderRadius: 3 }
                    }}
                  />
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>Quality Over Time</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Assessment scores and volume over the selected time range
            </Typography>
            <Box sx={{ height: 300, mt: 2 }}>
              {qualityTrend.labels.length > 0 ? (
                <Line data={qualityTrend} options={chartOptions} />
              ) : (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', pt: 10 }}>
                  No trend data available for the selected time range
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>

      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>Top Failing Rules</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Rules with highest failure rates across all assessed entities
        </Typography>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Rule Name</TableCell>
                <TableCell align="right">Failures</TableCell>
                <TableCell align="right">Impact</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(dashboardData.topRules || []).map((rule, index) => (
                <TableRow key={index} hover>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {rule.name}
                      {rule.errors > 50 && (
                        <Chip
                          label={rule.errors > 100 ? 'High Impact' : 'Medium Impact'}
                          size="small"
                          color={rule.errors > 100 ? 'error' : 'warning'}
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">{(rule.errors || 0).toLocaleString()}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <LinearProgress
                        variant="determinate"
                        value={Math.min(rule.impact || 0, 100)}
                        sx={{
                          width: 100, mr: 1, height: 8, borderRadius: 4,
                          backgroundColor: theme.palette.grey[200],
                          '& .MuiLinearProgress-bar': {
                            backgroundColor: rule.impact > 50 ? theme.palette.error.main : rule.impact > 20 ? theme.palette.warning.main : theme.palette.success.main,
                            borderRadius: 4
                          }
                        }}
                      />
                      <Typography variant="body2" sx={{ minWidth: 35, textAlign: 'right' }}>
                        {rule.impact}%
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {(!dashboardData.topRules || dashboardData.topRules.length === 0) && (
                <TableRow>
                  <TableCell colSpan={3} align="center">
                    <Typography variant="body2" color="text.secondary">No rule failure data available</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar(s => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default QualityDashboard;
